// Sweet Match - canvas rendering, tap input, HUD, and the GameDef export.
//
// All the rules live in state.ts; this file only draws what it is told and
// forwards taps. That split is why the headless bot in the validator can play
// the exact same game.
//
// Coordinates: the simulation speaks in candy diameters (see physics.ts). One
// scale factor `d` (px per unit) converts to pixels, picked to fit the play area
// in both directions - so on a short screen the board shrinks instead of
// overflowing.

import { el, clear } from "../../lib/dom";
import { sfx, haptic } from "../../lib/feedback";
import type { GameDef, LevelView, LevelViewOptions } from "../types";
import {
  CANDY_R,
  FIELD_H,
  FIELD_W,
  funnelSegments,
  limitLineY,
} from "./engine";
import {
  advanceState,
  createState,
  starsFor,
  tapAt,
  type Candy,
  type SmEvent,
  type SmState,
} from "./state";
import "./sweetmatch.css";

/** One glyph per candy type: colour alone is not enough (colour blindness), and
 *  it matches Sweet Swap's ● ■ ▲ ◆ ★ ♥ language. */
const GLYPHS = ["●", "■", "▲", "◆", "★", "♥", "✦", "▼"];
const MAX_FRAME_MS = 50;

// ---- Juice tuning (render-only; the physics never sees any of this) ----
/** A candy falling faster than this (units/step) that then all but stops has
 *  landed on something - that is what triggers the squash. Funnel sliding runs
 *  at 0.03-0.06, so this also fires on a candy joining the queue. */
const LAND_VY = 0.05;
/** ...and "all but stops" means slower than this. */
const LAND_STOP_VY = 0.02;
/** How long one squash & stretch takes to relax. */
const SQUASH_MS = 240;
/** Idle bob of untapped candies: amplitude in units (~1.5px on a phone). */
const WOBBLE_AMP = 0.02;
/** Angular rate divisor: sin(now / this) gives a ~4.4s period. */
const WOBBLE_MS = 700;

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number; // 1 -> 0
  color: string;
}

interface Pop {
  x: number;
  y: number;
  life: number;
  color: string;
}

function createLevelView(opts: LevelViewOptions): LevelView {
  let state: SmState = createState(opts.level);

  const root = opts.mount;
  clear(root);
  root.classList.add("sm");

  const canvas = el("canvas.sm-canvas") as HTMLCanvasElement;
  const stage = el("div.sm-stage", {}, canvas);
  const stat = el("span.sm-stat");
  const toolbar = el(
    "div.sm-toolbar",
    {},
    stat,
    el("button.sm-btn", { onclick: () => doRestart(), "aria-label": "Opnieuw" }, "↺ Opnieuw"),
  );
  root.append(stage, toolbar);

  const ctx = canvas.getContext("2d");
  const timers: Array<ReturnType<typeof setTimeout>> = [];
  const particles: Particle[] = [];
  const pops: Pop[] = [];
  let overlay: HTMLElement | null = null;
  let raf = 0;
  let last = performance.now();
  let finished = false;
  const reduceMotion =
    typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
  /** Per-body vertical speed of the previous frame, to spot the falling->stopped
   *  transition that means "just landed". */
  const lastVy = new Map<number, number>();
  /** Squashes in flight: when they started and how hard the landing was. */
  const squashes = new Map<number, { start: number; amt: number }>();

  // ---- Metrics & palette (recomputed on resize) ----
  let d = 0; // px per unit
  let colors: string[] = [];
  let ink = "#33284b";
  let wall = "#c99b62";
  let wallEdge = "#8c5f2e";
  /** Pre-rendered candy sprites, keyed `type` and "mystery". Canvas gradients are
   *  far too expensive to build per candy per frame. */
  let sprites = new Map<string, HTMLCanvasElement>();

  function readPalette(): void {
    const cs = getComputedStyle(root);
    colors = Array.from({ length: 8 }, (_, i) => cs.getPropertyValue(`--sm-${i + 1}`).trim() || "#999");
    ink = cs.getPropertyValue("--ink").trim() || "#33284b";
    wall = cs.getPropertyValue("--sm-wall").trim() || "#c99b62";
    wallEdge = cs.getPropertyValue("--sm-wall-edge").trim() || "#8c5f2e";
  }

  function layout(): void {
    const availW = stage.clientWidth;
    const availH = stage.clientHeight;
    if (availW === 0 || availH === 0) {
      // The stage has not been laid out yet. Re-schedule instead of computing a
      // garbage (or negative) size - same guard as bubblepop's layout().
      raf = requestAnimationFrame(layout);
      return;
    }
    d = Math.min(availW / FIELD_W, availH / FIELD_H);
    const dpr = Math.min(window.devicePixelRatio || 1, 2.5);
    canvas.style.width = `${FIELD_W * d}px`;
    canvas.style.height = `${FIELD_H * d}px`;
    canvas.width = Math.round(FIELD_W * d * dpr);
    canvas.height = Math.round(FIELD_H * d * dpr);
    ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);
    readPalette();
    sprites = new Map();
    // Paint immediately rather than waiting for the next animation frame: the
    // board should be on screen the moment it has a size. Also matters when the
    // tab is hidden, where rAF does not fire at all.
    draw(0);
  }

  const onResize = (): void => layout();
  window.addEventListener("resize", onResize);
  // The play area can be laid out after this runs and a resize event is not
  // guaranteed, so observe the stage rather than trusting one.
  const observer = new ResizeObserver(() => layout());
  observer.observe(stage);

  // ---- Candy sprites ----
  function candySprite(key: string, base: string): HTMLCanvasElement {
    const cached = sprites.get(key);
    if (cached) return cached;
    const size = Math.max(8, Math.ceil(CANDY_R * 2 * d) + 4);
    const c = document.createElement("canvas");
    c.width = size;
    c.height = size;
    const g = c.getContext("2d");
    const r = size / 2 - 2;
    if (g) {
      const grad = g.createRadialGradient(size * 0.34, size * 0.3, r * 0.1, size / 2, size / 2, r);
      grad.addColorStop(0, mix(base, "#ffffff", 0.45));
      grad.addColorStop(0.55, base);
      grad.addColorStop(1, mix(base, "#000000", 0.32));
      g.beginPath();
      g.arc(size / 2, size / 2, r, 0, Math.PI * 2);
      g.fillStyle = grad;
      g.fill();
      g.lineWidth = Math.max(1.5, r * 0.11);
      g.strokeStyle = mix(base, ink, 0.45);
      g.stroke();
      // Specular highlight, same place as Sweet Swap's.
      g.beginPath();
      g.ellipse(size * 0.37, size * 0.28, r * 0.32, r * 0.2, -0.5, 0, Math.PI * 2);
      g.fillStyle = "rgba(255,255,255,0.6)";
      g.fill();
    }
    sprites.set(key, c);
    return c;
  }

  /** Blend two hex/rgb colours. Keeps the sprite code independent of CSS
   *  color-mix(), which is not available inside canvas. */
  function mix(a: string, b: string, t: number): string {
    const pa = parseColor(a);
    const pb = parseColor(b);
    const ch = (i: number): number => Math.round(pa[i] + (pb[i] - pa[i]) * t);
    return `rgb(${ch(0)},${ch(1)},${ch(2)})`;
  }

  function parseColor(c: string): [number, number, number] {
    const hex = c.trim();
    if (hex.startsWith("#")) {
      const h = hex.length === 4 ? hex.replace(/#(.)(.)(.)/, "#$1$1$2$2$3$3") : hex;
      return [
        parseInt(h.slice(1, 3), 16),
        parseInt(h.slice(3, 5), 16),
        parseInt(h.slice(5, 7), 16),
      ];
    }
    const m = hex.match(/(\d+(?:\.\d+)?)/g);
    if (m && m.length >= 3) return [Number(m[0]), Number(m[1]), Number(m[2])];
    return [150, 150, 150];
  }

  // ---- Drawing ----
  function drawFunnel(g: CanvasRenderingContext2D): void {
    g.lineCap = "round";
    g.lineJoin = "round";
    const path = new Path2D();
    for (const s of funnelSegments()) {
      path.moveTo(s.ax * d, s.ay * d);
      path.lineTo(s.bx * d, s.by * d);
    }
    g.lineWidth = Math.max(5, 0.15 * d);
    g.strokeStyle = wallEdge;
    g.stroke(path);
    g.lineWidth = Math.max(3, 0.1 * d);
    g.strokeStyle = wall;
    g.stroke(path);
  }

  function drawLimitLine(g: CanvasRenderingContext2D, warning: boolean): void {
    const y = limitLineY() * d;
    g.save();
    g.setLineDash([6, 6]);
    g.lineWidth = 2;
    if (warning) {
      // Pulse so the grace period reads as "act now", not "you already lost".
      const t = 0.5 + 0.5 * Math.sin(performance.now() / 90);
      g.strokeStyle = `rgba(224, 69, 58, ${0.5 + 0.5 * t})`;
      g.lineWidth = 3;
    } else {
      g.strokeStyle = "rgba(128,120,150,0.35)";
    }
    g.beginPath();
    g.moveTo(0, y);
    g.lineTo(FIELD_W * d, y);
    g.stroke();
    g.restore();
  }

  function drawCandy(
    g: CanvasRenderingContext2D,
    candy: Candy,
    x: number,
    y: number,
    dropped: boolean,
    squash = 0,
  ): void {
    const shown = candy.revealed;
    const base = shown ? colors[candy.type % colors.length] : "#7c7590";
    const sprite = candySprite(shown ? `t${candy.type}` : "mystery", base);
    const px = x * d;
    const py = y * d;
    if (squash > 0) {
      // Squash & stretch, anchored at the bottom of the candy so it flattens
      // against the surface it just landed on instead of around its center.
      g.save();
      const anchor = py + CANDY_R * d;
      g.translate(px, anchor);
      g.scale(1 + 0.25 * squash, 1 - 0.3 * squash);
      g.translate(-px, -anchor);
    }
    g.drawImage(sprite, px - sprite.width / 2, py - sprite.height / 2);

    // Glyph: type identity that survives any palette, including the
    // colour-blind one.
    g.font = `600 ${Math.round(0.42 * d)}px system-ui, sans-serif`;
    g.textAlign = "center";
    g.textBaseline = "middle";
    g.fillStyle = "rgba(255,255,255,0.8)";
    g.fillText(shown ? GLYPHS[candy.type % GLYPHS.length] : "?", px, py + 0.01 * d);

    if (!dropped) {
      // Untapped candies float: a thin bright rim reads as "frozen in the air".
      g.beginPath();
      g.arc(px, py, CANDY_R * d - 1, 0, Math.PI * 2);
      g.lineWidth = Math.max(1.5, 0.05 * d);
      g.strokeStyle = "rgba(255,255,255,0.55)";
      g.stroke();
    }
    if (candy.frozen) {
      g.beginPath();
      g.arc(px, py, CANDY_R * d, 0, Math.PI * 2);
      g.fillStyle = "rgba(220,244,255,0.55)";
      g.fill();
      g.lineWidth = Math.max(2, 0.06 * d);
      g.strokeStyle = "rgba(255,255,255,0.9)";
      g.stroke();
      g.font = `600 ${Math.round(0.4 * d)}px system-ui, sans-serif`;
      g.fillStyle = "rgba(60,110,150,0.95)";
      g.fillText("❄", px, py + 0.02 * d);
    }
    if (squash > 0) g.restore();
  }

  function drawEffects(g: CanvasRenderingContext2D, dt: number): void {
    for (let i = pops.length - 1; i >= 0; i--) {
      const p = pops[i];
      p.life -= dt / 380;
      if (p.life <= 0) {
        pops.splice(i, 1);
        continue;
      }
      const r = CANDY_R * d * (1 + (1 - p.life) * 1.5);
      g.beginPath();
      g.arc(p.x * d, p.y * d, r, 0, Math.PI * 2);
      g.lineWidth = Math.max(2, 0.08 * d * p.life);
      g.strokeStyle = withAlpha(p.color, p.life * 0.8);
      g.stroke();
    }
    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i];
      p.life -= dt / 520;
      if (p.life <= 0) {
        particles.splice(i, 1);
        continue;
      }
      p.vy += 0.00007 * dt * dt;
      p.x += (p.vx * dt) / 16;
      p.y += (p.vy * dt) / 16;
      g.beginPath();
      g.arc(p.x * d, p.y * d, Math.max(1, 0.09 * d * p.life), 0, Math.PI * 2);
      g.fillStyle = withAlpha(p.color, p.life);
      g.fill();
    }
  }

  function withAlpha(color: string, a: number): string {
    const [r, g, b] = parseColor(color);
    return `rgba(${r},${g},${b},${Math.max(0, Math.min(1, a))})`;
  }

  function draw(dt: number): void {
    if (!ctx || d === 0) return;
    ctx.clearRect(0, 0, FIELD_W * d, FIELD_H * d);
    drawFunnel(ctx);
    drawLimitLine(ctx, state.warning);
    const now = performance.now();
    for (const body of state.world.bodies) {
      const candy = state.candies.get(body.id);
      if (!candy) continue;
      let y = body.y;
      let squash = 0;
      if (!reduceMotion) {
        if (body.dynamic) {
          // Landing detection: fell fast last frame, all but stopped now.
          // Verlet velocity (y - py) is per physics step, so it is framerate
          // independent; an impact collapses it within a single step.
          const vy = body.y - body.py;
          const prev = lastVy.get(body.id) ?? 0;
          lastVy.set(body.id, vy);
          if (prev > LAND_VY && vy < LAND_STOP_VY) {
            // Harder landings squash more; 0.2 units/step is near terminal.
            squashes.set(body.id, { start: now, amt: Math.min(1, prev / 0.2) });
          }
          const sq = squashes.get(body.id);
          if (sq) {
            const e = 1 - (now - sq.start) / SQUASH_MS;
            if (e <= 0) squashes.delete(body.id);
            else squash = sq.amt * e;
          }
        } else {
          // Idle wobble. Per-id phase so the field shimmers instead of pumping
          // in sync. Draw-only: taps still hit the body's true position, and
          // the offset is far below CANDY_R so aim is unaffected.
          y += WOBBLE_AMP * Math.sin(now / WOBBLE_MS + body.id * 2.4);
        }
      }
      drawCandy(ctx, candy, body.x, y, body.dynamic, squash);
    }
    drawEffects(ctx, dt);
  }

  // ---- Events from the rules ----
  function handle(events: SmEvent[]): void {
    for (const ev of events) {
      switch (ev.kind) {
        case "match": {
          // The bodies are already gone from the world; drop our render
          // bookkeeping for them too.
          for (const id of [ev.a.id, ev.b.id]) {
            lastVy.delete(id);
            squashes.delete(id);
          }
          const color = colors[ev.a.type % colors.length] ?? "#fff";
          pops.push({ x: ev.x, y: ev.y, life: 1, color });
          if (!reduceMotion) {
            for (let i = 0; i < 9; i++) {
              const angle = (i / 9) * Math.PI * 2;
              particles.push({
                x: ev.x,
                y: ev.y,
                vx: Math.cos(angle) * 0.055,
                vy: Math.sin(angle) * 0.055 - 0.02,
                life: 1,
                color,
              });
            }
          }
          sfx.merge();
          haptic([10, 20, 10]);
          break;
        }
        case "drop":
          sfx.tap();
          haptic(5);
          break;
        case "ice":
          // Ice cracks but the candy stays: a duller sound than a drop.
          sfx.place();
          haptic(8);
          break;
        case "reveal":
          break;
        case "warn":
          root.classList.toggle("is-warning", ev.on);
          if (ev.on) {
            sfx.error();
            haptic(18);
          }
          break;
        case "won":
          win();
          break;
        case "lost":
          lose(ev.reason);
          break;
      }
    }
  }

  // Called every frame, so skip the DOM write unless something actually changed.
  let lastStat = "";
  let lastRemaining = -1;
  function updateStatus(): void {
    const text = `Stapel: ${state.pressure} · Piek ${state.peak} / par ${state.level.peakPar}`;
    if (text !== lastStat) {
      lastStat = text;
      stat.textContent = text;
    }
    if (state.remaining !== lastRemaining) {
      lastRemaining = state.remaining;
      opts.setStatus?.(`🍭 ${state.remaining}`);
    }
  }

  function win(): void {
    if (finished) return;
    finished = true;
    sfx.win();
    haptic([20, 40, 20]);
    const stars = starsFor(state.peak, state.level.peakPar);
    timers.push(setTimeout(() => opts.onWin({ stars }), 460));
  }

  function lose(reason: "overflow" | "stuck"): void {
    if (finished || overlay) return;
    finished = true;
    sfx.lose();
    haptic([10, 30, 10]);
    root.classList.add("is-lost");
    opts.onLose?.(reason);
    const [title, body] =
      reason === "stuck"
        ? [
            "Muurvast! 🍬",
            "Geen enkel paar raakt elkaar nog: alleen snoepjes die elkaar écht raken verdwijnen. Probeer paren direct na elkaar te laten vallen.",
          ]
        : [
            "De pot loopt over! 🍯",
            "Er passen er geen meer in. Laat de snoepjes in een slimmere volgorde vallen.",
          ];
    overlay = el(
      "div.sm-overlay",
      {},
      el(
        "div.sm-overlay-card",
        {},
        el("h3", {}, title),
        el("p", {}, body),
        el("button.sm-btn.primary", { onclick: () => doRestart() }, "↺ Opnieuw"),
      ),
    );
    stage.append(overlay);
  }

  // ---- Input ----
  function onPointerDown(e: PointerEvent): void {
    if (finished || overlay || d === 0) return;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) / d;
    const y = (e.clientY - rect.top) / d;
    handle(tapAt(state, x, y)); // a miss returns no events, so it stays silent
    updateStatus();
  }
  canvas.addEventListener("pointerdown", onPointerDown);

  // ---- Loop ----
  function frame(now: number): void {
    const dt = Math.min(MAX_FRAME_MS, now - last);
    last = now;
    if (!finished) handle(advanceState(state, dt));
    updateStatus();
    draw(dt);
    raf = requestAnimationFrame(frame);
  }

  // ---- Restart ----
  function doRestart(): void {
    overlay?.remove();
    overlay = null;
    root.classList.remove("is-warning", "is-lost");
    particles.length = 0;
    pops.length = 0;
    lastVy.clear();
    squashes.clear();
    for (const t of timers) clearTimeout(t);
    timers.length = 0;
    finished = false;
    // Generation is a pure function of the level number, so this replays the
    // exact same board.
    state = createState(opts.level);
    updateStatus();
  }

  updateStatus();
  layout();
  last = performance.now();
  raf = requestAnimationFrame(frame);

  return {
    destroy() {
      cancelAnimationFrame(raf);
      for (const t of timers) clearTimeout(t);
      window.removeEventListener("resize", onResize);
      observer.disconnect();
      canvas.removeEventListener("pointerdown", onPointerDown);
    },
    restart: doRestart,
  };
}

export const sweetMatch: GameDef = {
  id: "sweetmatch",
  title: "Sweet Match",
  tagline: "Laat snoepjes vallen en match ze in de pot",
  // Violet: the remaining free hue. Sweet Swap already owns pink #ff6fae, and
  // its icon is 🍬 - these two sit next to each other in the catalog, so both
  // the colour and the icon have to differ.
  accent: "#8b5cf6",
  icon: "🍭",
  levelCount: Infinity,
  createLevelView,
};
